import React, { useEffect, useState } from 'react';
import { Typography, Box, Pagination, Stack } from '@mui/material';
import NewsCard from './NewsCard';
import { NewsArticle, AdPosition } from '../../types';
import { useErrorHandler } from '../../hooks';
import { LoadingSkeleton } from '../common';
import AdComponent from '../AdComponent';
import adService from '../../services/adService';
import { AD_CONFIG } from '../../config/adConfig';

interface NewsListProps {
  news: NewsArticle[];
  loading: boolean;
  error: string | null;
  pagination?: {
    count: number;
    page: number;
    onChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  };
  title?: string;
  emptyMessage?: string;
  showAds?: boolean; // Reklamları gösterip göstermeyeceğini belirleyen bayrak
}

const NewsList: React.FC<NewsListProps> = ({
  news,
  loading,
  error,
  pagination,
  title,
  emptyMessage = 'Bu kategoride henüz haber bulunmamaktadır.',
  showAds = true // Varsayılan olarak reklamları göster
}) => {
  // Handle any errors with toast notification
  useErrorHandler(error);
  
  // Reklam durumu için state
  const [listAds, setListAds] = useState<AdPosition[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);
  
  // İçerik alanı reklamlarını yükle
  useEffect(() => {
    if (!showAds) return;
    
    const fetchAds = async () => {
      try {
        setAdsLoading(true);
        // Mobil cihaz kontrolü yapmak için tarayıcı genişliğine bakılabilir
        const isMobile = window.innerWidth < 768;
        const screenSize = isMobile ? 'MOBILE' : 'DESKTOP';
        
        // Bu liste için kullanılabilecek bir kategori ID'si var mı?
        // Örneğin, bir kategori sayfasında ise o kategorinin ID'si kullanılabilir
        const categoryId = news[0]?.category?.id; // İlk haberin kategorisi, eğer varsa
        
        // Listeleme sayfaları için MIDDLE_CONTENT konumundaki akıllı reklamları al
        const positionAds = await adService.getSmartAds('MIDDLE_CONTENT', screenSize, undefined, categoryId);
        setListAds(positionAds);
        
      } catch (error) {
        console.error('Reklamlar yüklenirken hata oluştu:', error);
      } finally {
        setAdsLoading(false);
      }
    };
    
    // Haberler yüklendiyse reklamları getir
    if (news.length > 0) {
      fetchAds();
    }
  }, [showAds, news]);

  if (loading) {
    return <LoadingSkeleton variant="list" count={8} />;
  }

  if (news.length === 0) {
    return (
      <Box sx={{ my: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }
  
      // Reklam ayarları - konfigürasyondan alınıyor
    const NEWS_PER_AD = AD_CONFIG.frequency.newsListInterval;
    const MAX_ADS_PER_PAGE = AD_CONFIG.frequency.maxAdsPerPage;

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
          {title}
        </Typography>
      )}

      <Box>
        {(() => {
          const elements: React.ReactElement[] = [];
          
          news.forEach((article, index) => {
            // Haber kartını ekle
            elements.push(
              <Box 
                key={article.id}
                sx={{ 
                  display: 'inline-block',
                  width: { xs: '100%', sm: '47%', md: '31%', lg: '23%' },
                  mb: 4,
                  mr: { xs: 0, sm: 3, md: 3, lg: 3 },
                  verticalAlign: 'top'
                }}
              >
                <NewsCard article={article} />
              </Box>
            );
            
            // Her 8 haberde bir reklam ekle (son haber değilse) ve maksimum reklam sayısını aşma
            const adIndex = Math.floor((index + 1) / NEWS_PER_AD) - 1;
            if (showAds && listAds.length > 0 && 
                (index + 1) % NEWS_PER_AD === 0 && 
                (index + 1) < news.length && 
                adIndex < MAX_ADS_PER_PAGE) {
              const randomAd = listAds[Math.floor(Math.random() * listAds.length)];
              elements.push(
                <Box key={`ad-after-${index}`} sx={{ width: '100%', my: 4, display: 'block', clear: 'both' }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px',
                      backgroundColor: 'transparent',
                      borderRadius: 3,
                      position: 'relative',
                      mx: 'auto',
                      maxWidth: '100%',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '2px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '1px'
                      }
                    }}
                  >
                    <AdComponent 
                      adCode={randomAd.adCode}
                      clickUrl={randomAd.clickUrl}
                    />
                    <Typography 
                      variant="caption" 
                      color="text.disabled"
                      sx={{ 
                        position: 'absolute', 
                        top: -8, 
                        right: 0, 
                        fontSize: '9px',
                        opacity: 0.5,
                        fontWeight: 300,
                        letterSpacing: '0.5px'
                      }}
                    >
                      Reklam
                    </Typography>
                  </Box>
                </Box>
              );
            }
          });
          
          return elements;
        })()}
      </Box>

      {pagination && pagination.count > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={pagination.count}
            page={pagination.page}
            onChange={pagination.onChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default NewsList; 